// useModeKeyboard — global keyboard shortcuts for mode + camera.
//
// Mounted once at the app root. Listens on `window` for:
//
//   E      — toggle between narrative / exploration
//   R      — reset camera to scene 0 (intro) view
//   ESC    — dismiss open info card / panel
//   1..7   — jump to scene N (narrative mode only)
//   SPACE  — pause / resume auto-orbit (exploration only)
//
// The hook fires analytics events for the toggle + auto-orbit
// state changes. All shortcuts are no-ops if the user is typing in
// an input or contenteditable element.

import { useEffect } from 'react';
import { SCENES } from './planetSections';
import { track } from '../utils/analytics';

function isTypingTarget(el: EventTarget | null): boolean {
  if (!el || !(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;
  return false;
}

function readMode(): 'narrative' | 'exploration' | 'tour' {
  if (typeof window === 'undefined') return 'narrative';
  const v = window.__planetMode;
  return v === 'exploration' || v === 'tour' ? v : 'narrative';
}

function setMode(m: 'narrative' | 'exploration' | 'tour') {
  if (typeof window === 'undefined') return;
  if (window.__planetModeController) {
    window.__planetModeController.setMode(m);
  } else {
    window.__planetMode = m;
    try {
      window.localStorage.setItem('yhazrin:mode', m);
    } catch {
      /* ignore */
    }
  }
}

function scrollToScene(i: number) {
  if (typeof window === 'undefined') return;
  const s = SCENES[i];
  if (!s) return;
  const max = document.documentElement.scrollHeight - window.innerHeight;
  if (max <= 0) return;
  const target = (s.start + s.end) / 2;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({
    top: target * max,
    behavior: reduced ? 'auto' : 'smooth',
  });
}

export function useModeKeyboard(): void {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onKey = (e: KeyboardEvent) => {
      // Modifier-key combos (Cmd-R, Ctrl-1, etc.) — let the browser
      // handle those; we only act on bare keys.
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;

      // ESC — dismiss open info card / panel.
      if (e.key === 'Escape') {
        if (window.__planetDismissInfoCard) {
          window.__planetDismissInfoCard();
        }
        // Also dispatch the legacy close event so the existing HUD
        // panel close path runs.
        window.dispatchEvent(
          new CustomEvent('planet:open-panel', { detail: null }),
        );
        return;
      }

      // E — toggle mode (narrative ↔ exploration).
      if (e.key === 'e' || e.key === 'E') {
        const from = readMode();
        const to = from === 'exploration' ? 'narrative' : 'exploration';
        setMode(to);
        track('mode_toggle', { from, to });
        return;
      }

      // T — toggle auto-tour.
      if (e.key === 't' || e.key === 'T') {
        const ctrl =
          typeof window !== 'undefined' ? window.__planetTourController : null;
        if (ctrl) {
          ctrl.toggle();
        } else {
          // Fallback: just toggle the mode bit (controller will start
          // the tour if mounted; otherwise nothing happens).
          const from = readMode();
          setMode(from === 'tour' ? 'narrative' : 'tour');
        }
        return;
      }

      // R — reset camera to scene 0 view.
      if (e.key === 'r' || e.key === 'R') {
        if (readMode() === 'narrative') {
          scrollToScene(0);
        } else {
          // In exploration, just reset the orbit to a known pose.
          if (window.__planetDismissInfoCard) {
            window.__planetDismissInfoCard();
          }
        }
        return;
      }

      // SPACE — pause / resume auto-orbit (exploration only).
      if (e.code === 'Space' || e.key === ' ') {
        if (readMode() !== 'exploration') return;
        e.preventDefault();
        const cur = !!window.__planetAutoOrbitPaused;
        window.__planetAutoOrbitPaused = !cur;
        track(cur ? 'auto_orbit_resume' : 'auto_orbit_pause');
        return;
      }

      // 1..7 — jump to scene N (narrative only).
      if (readMode() !== 'narrative') return;
      const n = Number.parseInt(e.key, 10);
      if (Number.isFinite(n) && n >= 1 && n <= SCENES.length) {
        scrollToScene(n - 1);
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}

export default useModeKeyboard;